// Libraries
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';

// REDUX: Actions
import { connect } from 'react-redux';
import { modalActions } from '~/redux/actions/modal-actions';

let afterClose;

/**
 * Modal Wrapper
 * Handles ESC key pressing to close the dialog window
 *
 * @export
 * @class Modal
 * @extends {Component}
 */
class Modal extends PureComponent {
	constructor(props) {
		super(props);

		this.onModalDialogClick = this.onModalDialogClick.bind(this);
		this.onModalOverlayClick = this.onModalOverlayClick.bind(this);
	}

	listenKeyboard = event => {
		if (event.key === 'Escape' || event.keyCode === 27) {
			if (this.props.modal.properties.cancelHandle) {
				this.props.modal.properties.cancelHandle();
			}

			modalActions.hideModal();
			this.props.dispatch(modalActions.hideModal());

			if (afterClose) {
				afterClose();
			}
		}
	};

	componentDidMount() {
		window.addEventListener('keydown', this.listenKeyboard, true);
	}

	componentWillUnmount() {
		window.removeEventListener('keydown', this.listenKeyboard, true);
	}

	renderModalTitle(title, subtitle, large, center) {
		if (title && subtitle) {
			return (
				<React.Fragment>
					<Title id="title" large={large} center={center}>
						{title}
					</Title>
					<Subtitle id="subtitle" large={large} center={center}>
						{subtitle}
					</Subtitle>
				</React.Fragment>
			);
		} else if (title) {
			return <Title id="title">{title}</Title>;
		} else {
			return null;
		}
	}

	onModalOverlayClick = () => {
		modalActions.hideModal();
		this.props.dispatch(modalActions.hideModal());

		if (afterClose) {
			afterClose();
		}
	};

	onModalDialogClick = event => {
		event.stopPropagation();
	};

	render() {
		return (
			<div id="modal">
				<ModalOverlay id="modal-overlay" />
				<ModalContent id="modal-content" onClick={this.onModalOverlayClick}>
					<ModalDialog
						id="modal-dialog"
						className="modal__dialog"
						role="dialog"
						aria-labelledby="dialog__title"
						aria-modal="true"
						large={this.props.large}
						center={this.props.center}
						onClick={this.onModalDialogClick}
					>
						<Header>
							{this.renderModalTitle(
								this.props.modalTitle,
								this.props.modalSubtitle,
								this.props.large,
								this.props.center,
							)}
						</Header>
						<Body>{this.props.children}</Body>
						<CloseButton type="button" title="Close this window" onClick={this.onModalOverlayClick}>
							<svg xmlns="http://www.w3.org/2000/svg" data-name="Layer 1" viewBox="0 0 24 24">
								<path fill="none" d="M0 0h24v24H0z" data-name="Bounding Boxes" />
								<path
									fill="#686A87"
									d="M16.78 7.23a.75.75 0 0 0-1.07 0L12 10.93 8.29 7.22a.77.77 0 0 0-1.07 0 .77.77 0 0 0 0 1.07L10.93 12l-3.71 3.71a.77.77 0 0 0 0 1.07.77.77 0 0 0 1.07 0L12 13.07l3.71 3.71a.77.77 0 0 0 1.07 0 .77.77 0 0 0 0-1.07L13.07 12l3.71-3.71a.75.75 0 0 0 0-1.06z"
								/>
							</svg>
						</CloseButton>
					</ModalDialog>
				</ModalContent>
			</div>
		);
	}
}

/**
 * Whatever is returned will show up as props
 *
 * @param {any} state
 * @returns
 */
function mapStateToProps(state) {
	const { modal } = state;
	return {
		modal,
	};
}

////////////////////
// STYLED COMPONENTS
////////////////////

// Animations
const fadeBackground = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(4rem);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

// The Outer Overlay
const ModalOverlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	align-content: center;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	background-color: rgba(0, 0, 0, 0.3);
	-webkit-backdrop-filter: blur(2px);
	z-index: 1000;
	animation-name: ${fadeBackground};
	animation-duration: 480ms;
	animation-timing-function: ${props => props.theme.easeInOutCirc};
	cursor: pointer;
`;

// The Modal Content
const ModalContent = styled.div`
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	z-index: 10000;
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: flex-end;
	overflow: auto;
	text-align: center;
	-webkit-overflow-scrolling: touch;
	padding: 0;
	cursor: pointer;

	@media ${props => props.theme.breakpointLarge} {
		align-items: center;
	}

	&:after {
		vertical-align: middle;
		display: inline-block;
		height: 100%;
		margin-left: -0.05em;
		content: '';
	}
`;

// The dialog
const ModalDialog = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;

	box-sizing: border-box;
	width: 100%;
	height: auto;
	max-width: ${rem('740px')};
	padding-top: ${rem('24px')};
	padding-right: ${rem('24px')};
	padding-bottom: 0;
	padding-left: ${rem('24px')};

	cursor: default;
	animation-name: ${fadeInUp};
	animation-duration: 720ms;
	animation-timing-function: ${props => props.theme.easeOutExpo};
	text-align: center;
	vertical-align: middle;
	opacity: 1;
	outline: 0;
	border-top-left-radius: ${props => props.theme.globalRadius};
	border-top-right-radius: ${props => props.theme.globalRadius};
	border-bottom-left-radius: 0;
	border-bottom-right-radius: 0;
	background-color: ${props => props.theme.white};
	box-shadow: 0 10px 10px 0 rgba(0, 0, 0, 0.26), 0 14px 28px 0 rgba(0, 0, 0, 0.25);

	@media ${props => props.theme.breakpointLarge} {
		width: ${props => (props.large ? `100%` : `${rem('320px')}`)};
		padding-top: ${rem('32px')};
		padding-right: ${rem('32px')};
		padding-bottom: ${rem('32px')};
		padding-left: ${rem('32px')};
		border-radius: ${props => props.theme.globalRadius};
	}
`;

const CloseButton = styled.button`
	position: absolute;
	top: auto;
	bottom: ${rem('24px')};
	right: ${rem('16px')};
	border: none;
	padding: 0;
	background-color: ${props => props.theme.white};
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	width: ${rem('56px')};
	height: ${rem('56px')};
	border-radius: ${rem('56px')} !important;
	border: 1px solid ${props => props.theme.gray1};
	box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
	cursor: pointer;
	transition: all 200ms ease-out;

	svg {
		display: flex;
		width: ${rem('24px')};
		height: ${rem('24px')};
		margin: 0 auto;
	}

	&:hover,
	&:focus,
	&:active {
		transform: scale(1.05);
		border: 1px solid ${props => props.theme.gray2};
	}

	@media ${props => props.theme.breakpointLarge} {
		width: ${rem('32px')};
		height: ${rem('32px')};
		border-radius: ${rem('32px')} !important;
		top: ${props => props.theme.globalPadding};
		bottom: auto;
		box-shadow: none;
		border: 1px solid ${props => props.theme.white};
	}
`;

// The little icon that sits on top of the speech bubble
const Header = styled.header`
	padding: 0;
	margin: 0;
	width: 100%;
	margin-bottom: ${props => props.theme.globalMargin};
`;

// The Modal Title
const Title = styled.h2`
	text-align: ${props => (props.center ? `center` : `left`)};
	font-size: ${rem('22px')};
	font-weight: 700;
	margin: 0;
	color: ${props => props.theme.gray9};
`;

const Subtitle = styled.h5`
	text-align: ${props => (props.center ? `center` : `left`)};

	font-size: ${rem('16px')};
	font-weight: 300;
	margin: 0;
	color: ${props => props.theme.gray6};
`;

const Body = styled.div`
	padding-bottom: 0;
	width: 100%;
`;

Modal.propTypes = {
	children: PropTypes.node,
	modalTitle: PropTypes.string,
	modalSubtitle: PropTypes.string,
	onClose: PropTypes.func,
	large: PropTypes.bool,
	center: PropTypes.bool,
};

Modal.defaultProps = {
	modalTitle: 'Modal title',
	modalSubtitle: null,
	large: false,
};

export default connect(mapStateToProps)(Modal);



// WEBPACK FOOTER //
// ./src/components/general/modal/index.jsx